<template>
  <div class="markdown">
    <b-nav-item :to="{ path: `${makeLinkToAnchorCICD('github')}` }" class="md__link-wrapper">
      <h2 class="md__item-title">Oversecured Action for GitHub</h2>
    </b-nav-item>

    <p>
      This GitHub Action enables you to automatically upload your app versions to Oversecured for
      security scanning. An action user must have an active Integration.
    </p>

    <p>
      <strong>Important:</strong> We provide a ready-to-use GitHub Action available in the
      <a
        class="markdown__fit-link"
        href="https://github.com/marketplace/actions/oversecured-github"
        target="_blank"
        >GitHub Marketplace</a
      >. You can find the source code in our
      <a
        class="markdown__fit-link"
        href="https://github.com/oversecured/oversecured-github"
        target="_blank"
        >GitHub repository</a
      >.
    </p>

    <h3 id="overview">Overview</h3>
    <p>
      The Oversecured GitHub Action works for both iOS and Android applications. The main difference
      is how the application artifact is provided:
    </p>
    <ul>
      <li><strong>iOS:</strong> The project must be compressed into a ZIP archive</li>
      <li><strong>Android:</strong> The pipeline uploads the built APK or AAB directly</li>
    </ul>

    <h3 id="inputs">Action Inputs</h3>
    <p>The following inputs are available for the Oversecured GitHub Action:</p>
    <ul>
      <li>
        <code>access_token</code> (required) - Your Oversecured API key. A key should be generated
        in your
        <a
          class="markdown__fit-link"
          href="https://app.oversecured.com/settings/api-keys"
          target="_blank"
          >profile settings</a
        >.
      </li>
      <li>
        <code>integration_id</code> (required) - Integration ID, automatically generated when the
        Integration is created.
      </li>
      <li>
        <code>branch_name</code> (optional) - Branch name, <code>main</code> is default. You can
        create your own branches in the Integration settings.
      </li>
      <li>
        <code>app_path</code> (required) - The application file for scanning. Oversecured accepts
        APK/AAB files for Android and zipped sources for iOS.
      </li>
      <li>
        <code>delete_running</code> (optional) - If set to <code>true</code>, already running scans
        will be deleted. Default is <code>false</code>.
      </li>
    </ul>

    <h3 id="ios-setup">iOS Pipeline Setup</h3>

    <h4 id="ios-repository">Step 1: Add GitHub Actions Workflow</h4>
    <p>
      In your iOS repository, create a workflow file (e.g.,
      <code>.github/workflows/oversecured.yml</code>) and add the following configuration:
    </p>
    <pre
      v-pre
      class="language-yml"
    ><code class="language-yml"><span class="token key atrule">name</span><span class="token punctuation">:</span> Oversecured Scan

<span class="token key atrule">on</span><span class="token punctuation">:</span>
  <span class="token key atrule">push</span><span class="token punctuation">:</span>
    <span class="token key atrule">branches</span><span class="token punctuation">:</span> [ main ]
  <span class="token key atrule">workflow_dispatch</span><span class="token punctuation">:</span>

<span class="token key atrule">jobs</span><span class="token punctuation">:</span>
  <span class="token key atrule">build</span><span class="token punctuation">:</span>
    <span class="token key atrule">runs-on</span><span class="token punctuation">:</span> ubuntu<span class="token punctuation">-</span>latest
    <span class="token key atrule">steps</span><span class="token punctuation">:</span>
      <span class="token punctuation">-</span> <span class="token key atrule">name</span><span class="token punctuation">:</span> Checkout
        <span class="token key atrule">uses</span><span class="token punctuation">:</span> actions/checkout@v3

      <span class="token punctuation">-</span> <span class="token key atrule">name</span><span class="token punctuation">:</span> Pods Install
        <span class="token key atrule">run</span><span class="token punctuation">:</span> <span class="token punctuation">|</span>
          pod install

      <span class="token punctuation">-</span> <span class="token key atrule">name</span><span class="token punctuation">:</span> Zip Sources
        <span class="token key atrule">run</span><span class="token punctuation">:</span> <span class="token punctuation">|</span>
          zip <span class="token punctuation">-</span>q <span class="token punctuation">-</span>r OversecuredZipped.zip .

      <span class="token punctuation">-</span> <span class="token key atrule">name</span><span class="token punctuation">:</span> Oversecured Scanner
        <span class="token key atrule">uses</span><span class="token punctuation">:</span> oversecured/oversecured<span class="token punctuation">-</span>github@v1
        <span class="token key atrule">with</span><span class="token punctuation">:</span>
          <span class="token key atrule">access_token</span><span class="token punctuation">:</span> ${{ secrets.OVERSECURED_API_KEY }}
          <span class="token key atrule">integration_id</span><span class="token punctuation">:</span> ${{ vars.OVERSECURED_INTEGRATION_ID }}
          <span class="token key atrule">branch_name</span><span class="token punctuation">:</span> ${{ vars.OVERSECURED_BRANCH_NAME }}
          <span class="token key atrule">app_path</span><span class="token punctuation">:</span> OversecuredZipped.zip</code></pre>

    <p><strong>Key points for iOS:</strong></p>
    <ul>
      <li>The workflow compresses the entire repository into a ZIP file</li>
      <li>
        The ZIP file name must match the <code>app_path</code> value in the Oversecured Scanner step
      </li>
      <li>Make sure to install dependencies (like CocoaPods) before zipping if needed</li>
    </ul>

    <h4 id="ios-platform">Step 2: Configure Platform Settings</h4>

    <h5>Create API Key</h5>
    <ol>
      <li>
        Go to
        <a
          class="markdown__fit-link"
          href="https://app.oversecured.com/settings/api-keys"
          target="_blank"
          >Oversecured Settings → API Keys</a
        >
      </li>
      <li>Generate a new API key</li>
      <li>Copy the value</li>
    </ol>

    <h5>Create iOS Integration</h5>
    <ol>
      <li>Go to <strong>Apps</strong></li>
      <li>Click <strong>Add new app</strong></li>
      <li>Choose <strong>iOS</strong> platform</li>
      <li>Name it however you want</li>
      <li>Submit the request</li>
      <li>After creation, copy the integration ID from the UI or URL</li>
    </ol>

    <h5>Add GitHub Secrets and Variables</h5>
    <ol>
      <li>Open your GitHub repository</li>
      <li>Go to <strong>Settings</strong> tab</li>
      <li>
        Select <strong>Secrets and variables</strong> → <strong>Actions</strong> from the left
        sidebar
      </li>
      <li>
        Add a new repository secret:
        <ul>
          <li>Name: <code>OVERSECURED_API_KEY</code></li>
          <li>Value: Your API key</li>
        </ul>
      </li>
      <li>
        Add a new repository variable:
        <ul>
          <li>Name: <code>OVERSECURED_INTEGRATION_ID</code></li>
          <li>Value: Your integration ID</li>
        </ul>
      </li>
      <li>
        Optionally add <code>OVERSECURED_BRANCH_NAME</code> variable (defaults to
        <code>main</code> if not set)
      </li>
    </ol>

    <p>
      <strong>Note:</strong> Secrets are encrypted and only visible to GitHub Actions. Variables are
      visible in logs but can be used for non-sensitive data like integration IDs.
    </p>

    <h4 id="ios-run">Step 3: Commit and Run</h4>
    <p>
      After committing and pushing the workflow file, GitHub Actions will automatically start the
      workflow:
    </p>
    <ul>
      <li>The workflow checks out your code</li>
      <li>Dependencies are installed (if needed)</li>
      <li>The project is compressed into a ZIP file</li>
      <li>The Oversecured Scanner step uploads the archive to Oversecured</li>
      <li>A new scan version is created</li>
    </ul>
    <p>
      You can verify the scan by going to the Oversecured platform and opening your app. You should
      see a new scan in progress.
    </p>

    <h3 id="android-setup">Android Pipeline Setup</h3>

    <h4 id="android-repository">Step 1: Add GitHub Actions Workflow</h4>
    <p>
      For Android, you typically already have a build workflow. Add the Oversecured Scanner step to
      your existing workflow or create a new one:
    </p>
    <pre
      v-pre
      class="language-yml"
    ><code class="language-yml"><span class="token key atrule">name</span><span class="token punctuation">:</span> Oversecured Scan

<span class="token key atrule">on</span><span class="token punctuation">:</span>
  <span class="token key atrule">push</span><span class="token punctuation">:</span>
    <span class="token key atrule">branches</span><span class="token punctuation">:</span> [ main ]
  <span class="token key atrule">workflow_dispatch</span><span class="token punctuation">:</span>

<span class="token key atrule">jobs</span><span class="token punctuation">:</span>
  <span class="token key atrule">build</span><span class="token punctuation">:</span>
    <span class="token key atrule">runs-on</span><span class="token punctuation">:</span> macos<span class="token punctuation">-</span>latest
    <span class="token key atrule">steps</span><span class="token punctuation">:</span>
      <span class="token punctuation">-</span> <span class="token key atrule">name</span><span class="token punctuation">:</span> Checkout
        <span class="token key atrule">uses</span><span class="token punctuation">:</span> actions/checkout@v3

      <span class="token punctuation">-</span> <span class="token key atrule">name</span><span class="token punctuation">:</span> Change wrapper permissions
        <span class="token key atrule">run</span><span class="token punctuation">:</span> chmod +x ./gradlew

      <span class="token punctuation">-</span> <span class="token key atrule">name</span><span class="token punctuation">:</span> Build gradle project
        <span class="token key atrule">run</span><span class="token punctuation">:</span> ./gradlew build

      <span class="token punctuation">-</span> <span class="token key atrule">name</span><span class="token punctuation">:</span> Build debug apk
        <span class="token key atrule">run</span><span class="token punctuation">:</span> ./gradlew assembleDebug

      <span class="token punctuation">-</span> <span class="token key atrule">name</span><span class="token punctuation">:</span> Oversecured Scanner
        <span class="token key atrule">uses</span><span class="token punctuation">:</span> oversecured/oversecured<span class="token punctuation">-</span>github@v1
        <span class="token key atrule">with</span><span class="token punctuation">:</span>
          <span class="token key atrule">access_token</span><span class="token punctuation">:</span> ${{ secrets.OVERSECURED_API_KEY }}
          <span class="token key atrule">integration_id</span><span class="token punctuation">:</span> ${{ vars.OVERSECURED_INTEGRATION_ID }}
          <span class="token key atrule">branch_name</span><span class="token punctuation">:</span> ${{ vars.OVERSECURED_BRANCH_NAME }}
          <span class="token key atrule">app_path</span><span class="token punctuation">:</span> ./app/build/outputs/apk/debug/app<span class="token punctuation">-</span>debug.apk</code></pre>

    <p><strong>Key points for Android:</strong></p>
    <ul>
      <li>No compression is needed - the workflow uploads the built APK or AAB directly</li>
      <li>The <code>app_path</code> should point to the relative path of your APK/AAB file</li>
      <li>
        Example paths:
        <ul>
          <li>Debug APK: <code>./app/build/outputs/apk/debug/app-debug.apk</code></li>
          <li>Release APK: <code>./app/build/outputs/apk/release/app-release.apk</code></li>
          <li>AAB: <code>./app/build/outputs/bundle/release/app-release.aab</code></li>
        </ul>
      </li>
    </ul>

    <h4 id="android-platform">Step 2: Configure Platform Settings</h4>
    <p>Repeat the same steps as for iOS:</p>
    <ol>
      <li>Create a new app in Oversecured platform</li>
      <li>Choose <strong>Android</strong> platform</li>
      <li>Copy the integration ID</li>
      <li>
        Add GitHub Secrets and Variables:
        <ul>
          <li>Secret: <code>OVERSECURED_API_KEY</code> (same as iOS, or create a new one)</li>
          <li>Variable: <code>OVERSECURED_INTEGRATION_ID</code> (your Android integration ID)</li>
          <li>Variable (optional): <code>OVERSECURED_BRANCH_NAME</code></li>
        </ul>
      </li>
    </ol>

    <h4 id="android-run">Step 3: Commit and Run</h4>
    <p>After committing the workflow changes:</p>
    <ul>
      <li>The Android build job runs and produces the APK/AAB</li>
      <li>The Oversecured Scanner step uploads the file to Oversecured</li>
      <li>A new scan is created on the platform</li>
    </ul>
    <p>
      You can verify in both GitHub Actions logs (which show the scan progress) and the Oversecured
      UI (which shows the scan in progress).
    </p>

    <h3 id="summary">Summary</h3>
    <p>To summarize the key points:</p>
    <ul>
      <li><strong>iOS:</strong> Requires ZIP archive compression before upload</li>
      <li><strong>Android:</strong> Uploads build output (APK/AAB) directly</li>
      <li><strong>Secrets:</strong> Use GitHub Secrets for sensitive data like API keys</li>
      <li>
        <strong>Variables:</strong> Use GitHub Variables for non-sensitive data like integration IDs
      </li>
      <li>
        <strong>Action:</strong> Use <code>oversecured/oversecured-github@v1</code> from
        <a
          class="markdown__fit-link"
          href="https://github.com/marketplace/actions/oversecured-github"
          target="_blank"
          >GitHub Marketplace</a
        >
      </li>
      <li>
        <strong>Repository:</strong> Source code available at
        <a
          class="markdown__fit-link"
          href="https://github.com/oversecured/oversecured-github"
          target="_blank"
          >oversecured/oversecured-github</a
        >
      </li>
    </ul>
  </div>
</template>

<script>
import { makeAnchor, makeLinkToAnchor, makeLinkToAnchorCICD } from '../../../services/functions';
import { makeSubLinkToAnchor } from '@/services/functions';

export default {
  methods: {
    makeAnchor,
    makeLinkToAnchor,
    makeSubLinkToAnchor,
    makeLinkToAnchorCICD,
  },
};
</script>

<style scoped></style>
